import { useCallback, useMemo } from "react";
import { Alert, Checkbox, Stack } from "@mantine/core";
import { useAuth } from "../../hooks/useAuth";
import { useSetSpecimenInsufficientTissue } from "../../hooks/useWorkflows";
import {
  insufficientTissueError,
  InsufficientTissueSpecimen,
} from "../../services/workflows.service";

interface Props {
  caseId: string;
  specimen: InsufficientTissueSpecimen;
  stage: "grossing" | "embedding";
  disabled?: boolean;
}

export function SpecimenInsufficientTissueCheckbox({
  caseId,
  specimen,
  stage,
  disabled,
}: Props) {
  const { user } = useAuth();
  const mutation = useSetSpecimenInsufficientTissue();
  const allowed = useMemo(
    () => user?.permissions?.includes(`${stage}.write`),
    [user, stage]
  );
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      mutation.mutate({
        caseId,
        specimenId: specimen.id,
        stage,
        insufficientTissue: event.currentTarget.checked,
        expectedVersion: specimen.insufficientTissueVersion,
      });
    },
    [caseId, specimen, stage, mutation]
  );
  const error = useMemo(
    () =>
      mutation.error ? insufficientTissueError(mutation.error) : undefined,
    [mutation.error]
  );
  if (!allowed) return null;
  return (
    <Stack gap="xs">
      <Checkbox
        label="Insufficient tissue"
        aria-label={`Specimen ${specimen.number}: Insufficient tissue`}
        checked={specimen.insufficientTissue === true}
        onChange={handleChange}
        disabled={disabled || mutation.isPending}
      />
      {error && (
        <Alert
          color="red"
          variant="light"
          title="Could not save specimen flag"
          role="alert"
        >
          {error}
        </Alert>
      )}
    </Stack>
  );
}
